import { SpatialIndex } from 'features/builder/types'
import { ReactNode } from 'react'
import { ApiError } from 'features/common'
import { Connection, ConnectionParams, ProviderType } from 'features/connections'
import { Client } from 'features/common/utils/clientParameter'
import { SchemaMap, SchemaPreview } from 'features/jobs/types'
import { DOType, Subscription } from 'features/data-observatory/types'
import { DataObservatoryTreeItem } from 'features/data-observatory/ui/DataObservatoryPanel'
import { ImportFileType } from 'features/common/types'
import { GeomType } from 'features/maps/types'

export interface TilesetRequest {
  connectionId: string
  tilesetId: string
}

export enum SchemaFieldType {
  Number = 'number',
  Bigint = 'bigint',
  String = 'string',
  Geometry = 'geometry',
  Timestamp = 'timestamp',
  Object = 'object',
  Boolean = 'boolean',
  Variant = 'variant',
  Unknown = 'unknown'
}

export interface SchemaField {
  name: string
  type: SchemaFieldType
  artificial?: boolean
  isFilterColumn?: boolean
  description?: string
  agg_method?: string
  slug?: string
}

export enum DataOptimizationType {
  Optimize = 'optimize',
  Prepare = 'prepare'
}

export interface DataExplorerTableAction {
  type: 'cluster' | 'search optimization' | 'sortkey' | 'index' | 'srid'
  enabled: boolean
}

export enum DataExplorerTableOptimizationActionType {
  CreateTable = 'createTable',
  AlterTable = 'alterTable'
}

export interface DataExplorerTableOptimization {
  actions: DataExplorerTableAction[]
  actionAvailable?: {
    msg: string
    type: DataExplorerTableOptimizationActionType
    query: string
  }
}

export interface DataExplorerTable {
  provider: ProviderType
  id: string
  type: string
  lastModified?: string
  nrows: number
  size?: number
  geomType?: GeomType
  spatialIndexStats?: TableResourceStats['spatialIndex']
  geomField: string
  name: string
  schema: SchemaField[]
  _links?: Record<string, string>
  fqn: string
  isSample?: boolean
  isSubscription?: boolean
  slug?: string
  doDatasetId?: string
  subscription?: DataExplorerSubscription
  tableRegion?: string
  optimization?: DataExplorerTableOptimization
  metadata?: RasterMetadata
}

export const defaultGeomColumn = 'geom'
export const cartoDWSharedFolder = 'shared'

export enum TreeType {
  EXPLORER = 'explorer',
  OBSERVATORY = 'observatory'
}

export enum DataExplorerSubscriptionDataType {
  Data = 'data',
  Geography = 'geography'
}

export interface DataExplorerSubscription {
  associatedGeography: {
    id: string
    nrows: number
    size: number
  }
  enableExport: boolean
  datasetType: DataExplorerSubscriptionDataType
  entitlements: number
  expiresAt: string
  nrows: number
  size: number
  subscriptionType: SubscriptionTypes
  spatialIndexType?: string
  spatialIndexResolution?: number
}

export interface DataTreeNodeRenderProps {
  id: string
  label: string
  version?: string
  connectionId?: string
  connectionName?: string
  connectionProvider?: string
  connectionProviderId?: ProviderType
  connectionPrivacy?: string
  connectionImportsAvailable?: boolean
  fqn?: string
  icon?: ReactNode
  expandable?: boolean
  selectable?: boolean
  children?: DataTreeNodeChildren | DataTreeNodeRenderProps[]
  totalChildren?: number
  unknownTotal?: boolean
  page?: number
  error?: Error
  loading?: boolean
  data?: unknown
  statusIcon?: ReactNode
  disabled?: boolean
  hidden?: boolean
  treeItem?: DataExplorerTreeItem
  path?: string
  type?: string
  isEmpty?: boolean
}

export type DataTreeNodeChildren = DataTreeNode[]

export type DataTreeNode = string

export type DataExplorerTreeItemChildren = Record<string, DataExplorerTreeItem>
// THIS is kind'a Connection with DataExplorerResource and children, consider better typing
export interface DataExplorerTreeItem {
  id: string
  name?: string
  fqn?: string
  type: string
  provider_id?: string
  _links?: Record<string, string>
  connectionId?: string
  databaseName?: string
  hidden?: boolean
  schema?: SchemaField[]
  carto_dw?: boolean
  doLocation?: string
  icon?: ReactNode
  config?: {
    type: string
    billing_project?: string
    credentials: ConnectionParams
  }
  children?: DataExplorerTreeItemChildren | ApiError
  totalChildren?: number
  page: number
  location?: string
  isEmpty?: boolean
  unknownTotal?: boolean
}

export interface DataExplorerResource {
  id: string
  type?: string
  name?: string
  provider?: string
  geomField?: string
  children?: Array<DataExplorerResource>
  totalChildren?: number
  page?: number
  _links?: Record<string, string>
  isEmpty?: boolean
  unknownTotal?: boolean
  schema?: SchemaField[]
  metadata?: RasterMetadata
}

export enum DataTreeItemTypes {
  Connection = 'connection',
  Project = 'project',
  Dataset = 'dataset',
  Tileset = 'tileset',
  Table = 'table',
  Data = 'data',
  Geography = 'geography',
  Schema = 'schema',
  Database = 'database',
  Raster = 'raster'
}

export interface DataTreeResourceRequestProps {
  connectionId: string
  targetKey: string
  projectId?: string
  datasetId?: string
  databaseName?: string
  slug?: string
  isSample?: boolean
  isSubscription?: boolean
  search?: string
  page?: number
  pageSize?: number
  isEmpty?: boolean
}

export interface DataTreeSubscriptionInfoRequestProps {
  datasetId: string
  subscriptionTable: string
  slug: string
  spatialIndexType?: string
  spatialIndexResolution?: number
}

interface TilesetTilestatsLayer {
  count: number
  geometry: string
}
interface TilesetTilestats {
  layers?: [TilesetTilestatsLayer]
}

export enum TilesetsTypes {
  AGGREGATION = 'aggregation',
  SIMPLE = 'simple'
}

export enum MaxTileSizeStrategy {
  DropFeatures = 'drop_features',
  DropFractionAsNeeded = 'drop_fraction_as_needed',
  ReturnNull = 'return_null',
  ThrowError = 'throw_error'
}

export interface TilesetMetadata {
  carto_tileset: string
  count?: string
  features_count?: string
  geom: string
  tileset_type: TilesetsTypes
  minzoom?: number
  maxzoom?: number
  bounds?: string
  center?: string
  tilestats?: TilesetTilestats
  updated_at: string
  // TODO remove when backend fix it
  spatial_index?: SpatialIndex
}

export interface Tileset {
  created_at: string
  description: string
  fully_qualified_name: string
  metadata: TilesetMetadata
  name: string
  updated_at: string
  type: string
  id: string
  tableId?: string
  provider: string
  _links: {
    tileset: string
  }
}

export interface RasterMetadata {
  bands: {
    type: string
    name: string
    stats: {
      min: number
      max: number
    }
  }[]
  bounds: [number, number, number, number]
  center: [number, number, number]
  width: number
  height: number
  block_width: number
  block_height: number
  num_blocks: number
  num_pixels: number
  resolution?: number
  block_resolution?: number
  minresolution?: number
  maxresolution?: number
  nodata?: number | null
  pixel_resolution?: number
}

export interface Raster {
  created_at: string
  description: string
  fully_qualified_name: string
  metadata: RasterMetadata
  name: string
  updated_at: string
  type: string
  id: string
  tableId?: string
  provider: string
  _links: {
    stats: string
  }
}

export interface TilesetSizeByZoom {
  z: number
  avg_size: number
  max_size: number
}

export interface TableResourceStats {
  geomType?: GeomType
  spatialIndex?: {
    type: SpatialIndex.H3 | SpatialIndex.QUADBIN
    dataResolution: number
  }
}

export interface TilesetResourceStats {
  name: string
  by_zoom_level: TilesetSizeByZoom[]
}
export enum TreeObjectType {
  Tileset = 'tileset',
  Table = 'table',
  Raster = 'raster'
}

export interface TablePreviewOpts {
  connectionName: string
  provider: ProviderType
  table: string
  schema: SchemaField[]
  client?: Client
  refresh?: boolean
}

export interface PreviewGeomField {
  type: string
  coordinates: string[]
}

export interface DataTablePreview {
  data: Record<string, string | PreviewGeomField>[]
  schema: SchemaField[]
}

export interface OptimizeTableLocation {
  name: string
  connection: Connection
}

export enum ImportSourceType {
  LOCAL_FILE = 0,
  URL = 1
}

export { ImportFileType }

export interface ResourceLocation {
  name: string
  connection?: Connection
}

export interface ImportLocationFormPartial {
  connection: Connection
  targetFolderId: string
  name: string
  overwrite: boolean
}

export interface ImportFileForm extends ImportLocationFormPartial {
  type: ImportSourceType
  file: File | URL
  fileType: ImportFileType
  createLayerAfterJobImportedForMap?: string
  autoguessing: boolean
  schema: SchemaMap
  preview: SchemaPreview
  hasError: boolean
  errorMessage?: string
  continueWithoutPreview: boolean
  closeConfirmationOpen: boolean
  closeConfirmationFn: () => void
}

export interface DataExplorerNavigationProps {
  tree: Tab
  folder: string
  resource: string
}

// This enum must be integer mapping, it's used for Accordion
export enum Tab {
  EXPLORER,
  OBSERVATORY,
  SOURCES,
  COMPONENTS
}

export const CARTO_DW = 'CARTO Data Warehouse'
export enum ConnectionType {
  CartoDw = 'carto_dw'
}

export enum FieldType {
  Variant = 'variant'
}

export enum CreateButtonActions {
  Map = 'map',
  Tileset = 'tileset',
  Geocode = 'geocode'
}

export enum LatLngSelectTypes {
  Latitude = 'latitude',
  Longitude = 'longitude'
}

export enum AggregationMethods {
  SUM = 'Sum',
  MIN = 'Min',
  MAX = 'Max',
  AVG = 'Avg',
  COUNT = 'Count'
}

export enum AggregationOutputFormats {
  Geometries = 'geometries',
  H3 = 'h3',
  Quadbin = 'quadbin'
}

export enum AggregationGeometriesPlacement {
  CellCentroid = 'cell-centroid',
  Cell = 'cell',
  FeaturesAny = 'features-any',
  FeaturesCentroid = 'features-centroid'
}

export type onDragStartType = {
  event: React.DragEvent<HTMLLIElement>
  item: DataExplorerTreeItem
}

export type onSourceDODragStartType = {
  event: React.DragEvent<HTMLLIElement>
  slug: string
  label?: string
  datasetType?: CatalogEntityTypes
  doType?: DOType
  isPublic?: boolean
  geomType?: string
  datasetProvider?: string
  connectionProvider?: ProviderType
  size?: number
  isSample?: boolean
  isSubscription?: boolean
  subscription: Subscription | DataObservatoryTreeItem
}

export enum EventsDataTransfer {
  type = 'application/reactflow-type',
  data = 'application/reactflow-data'
}

export type DataExplorerDataSet = DataExplorerTable | Tileset | Raster

export enum Status {
  WaitingSelectObject,
  FetchingMetadata,
  ReadyToAdd,
  ReadyToAddTable,
  ReadyToAddTileset,
  ReadyToAddAsQuery,
  ReadyToAddSpatialIndex,
  Adding,
  GeoColumnNotFound,
  OptimizationAvailable,
  OptimizationMandatory,
  RasterNotSupported,
  RasterWithoutStats
}

export enum DisplayMode {
  Tree,
  List
}

export interface Dataset {
  id: string
  slug: string
  name: string
  countryName: string
  categoryName: string
  providerName: string
  version: string
  sampleInfo?: {
    id: string
    source?: string
  }
}

export interface DatasetVariable {
  name: string
  slug: string
  aggMethod: string
  columnName: string
  dbType: SchemaFieldType
  description: string
  isFilter: boolean
}

export type DatasetVariables = Array<DatasetVariable>

export enum CatalogEntityTypes {
  Dataset = 'dataset',
  Geography = 'geography'
}

export enum SubscriptionTypes {
  Public = 'public',
  Premium = 'premium'
}
